import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Button, Card, CardContent, CardHeader, CardTitle, Input, PipelineBadge } from '@agms/ui'
import type { PipelineStage } from '@agms/ui'
import { PIPELINE_STAGES, LEAD_SOURCES, OWNERSHIP_TYPES, manualTransitionTargets } from '../../../hooks/useLeads'
import { formatStage, formatLabel, stageColor, STAGE_BORDER_COLORS, formatCurrencyOrDash } from '../../../lib/format'
import { StageContextCard } from '../../lead/StageContextCard'
import { WhatsNextPanel } from '../../lead/WhatsNextPanel'
import { InlineAssigneeSelect } from '../../lead/InlineAssigneeSelect'
import { ApplicationDataCard } from '../../lead/ApplicationDataCard'
import { BoardingReadinessPanel } from '../../lead/BoardingReadinessPanel'
import { DocumentCenter } from '../../lead/DocumentCenter'
import { PendingDocumentRequests } from '../../lead/PendingDocumentRequests'
import { AccountActivityPanel } from '../AccountActivityPanel'
import { AppointmentsPanel } from '../AppointmentsPanel'
import { NotesPanel } from '../NotesPanel'
import { CreateTicketModal } from '../../tickets/CreateTicketModal'
import { PageHeader } from '../../ui'
import { usePermissions } from '../../../hooks/useAuth'
import { LeadStatementAnalyses } from './LeadStatementAnalyses'
import type { Lead, PipelineEvent } from '../../../lib/api'

type UpdateLeadMutation = {
  isPending: boolean
}

type DeleteLeadMutation = {
  isPending: boolean
}

type TransitionLeadMutation = {
  isPending: boolean
}

export interface LeadDetailContentProps {
  lead: Lead
  navigate: (to: string) => void
  canUpdateLeads: () => boolean
  canDeleteLeads: () => boolean
  canTransitionStage: () => boolean
  canAssignLeads: () => boolean
  canCreateTickets: () => boolean
  updateLead: UpdateLeadMutation
  deleteLead: DeleteLeadMutation
  transitionStage: TransitionLeadMutation
  isEditing: boolean
  setIsEditing: (value: boolean) => void
  showDeleteConfirm: boolean
  setShowDeleteConfirm: (value: boolean) => void
  showTransitionModal: boolean
  setShowTransitionModal: (value: boolean) => void
  showCreateTicketModal: boolean
  setShowCreateTicketModal: (value: boolean) => void
  transitionNote: string
  setTransitionNote: (value: string) => void
  selectedStage: string
  setSelectedStage: (value: string) => void
  transitionError: string | null
  handleSubmit: (e: React.FormEvent<HTMLFormElement>) => void
  handleDelete: () => void
  handleAssigneeChange: (slot: 'assigned_to' | 'assigned_to_secondary', userId: string | null) => void
  openStageModal: () => void
  handleTransition: () => void
  formatDate: (dateStr: string) => string
  showHeader?: boolean
  showSharedSections?: boolean
}

export function LeadDetailContent({
  lead,
  navigate,
  canUpdateLeads,
  canDeleteLeads,
  canTransitionStage,
  canAssignLeads,
  canCreateTickets,
  updateLead,
  deleteLead,
  transitionStage,
  isEditing,
  setIsEditing,
  showDeleteConfirm,
  setShowDeleteConfirm,
  showTransitionModal,
  setShowTransitionModal,
  showCreateTicketModal,
  setShowCreateTicketModal,
  transitionNote,
  setTransitionNote,
  selectedStage,
  setSelectedStage,
  transitionError,
  handleSubmit,
  handleDelete,
  handleAssigneeChange,
  openStageModal,
  handleTransition,
  formatDate,
  showHeader = true,
  showSharedSections = true,
}: LeadDetailContentProps) {
  const permissions = usePermissions()
  const canUseStatementAnalysis = permissions?.canUseStatementAnalysis?.() ?? false

  return (
      <div className="space-y-6">
        {showHeader && (
        <div>
          <div className="mb-2 flex items-center gap-4">
            <Link to="/leads" className="text-gray-500 hover:text-gray-700">
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
            </Link>
          </div>
          <PageHeader
            title={lead.dba_name || lead.legal_name || 'Unnamed Lead'}
            subtitle={`Created ${formatDate(lead.created_at)}`}
            action={(
          <div className="flex items-center gap-3">
            {canTransitionStage() && manualTransitionTargets(lead.stage).length > 0 ? (
              <button
                type="button"
                onClick={openStageModal}
                title="Click to change pipeline stage"
                className="cursor-pointer rounded-full transition-transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              >
                <PipelineBadge stage={lead.stage as PipelineStage} />
              </button>
            ) : (
              <PipelineBadge stage={lead.stage as PipelineStage} />
            )}
            {canTransitionStage() && manualTransitionTargets(lead.stage).length > 0 && (
                <Button variant="secondary" onClick={openStageModal}>
                  Change Stage
                </Button>
            )}
            {canUpdateLeads() && !isEditing && (
              <Button onClick={() => setIsEditing(true)}>Edit Lead</Button>
            )}
            {canCreateTickets() && (
              <Button variant="secondary" onClick={() => setShowCreateTicketModal(true)}>Create Ticket</Button>
            )}
            {canDeleteLeads() && (
              <Button variant="ghost" onClick={() => setShowDeleteConfirm(true)}>
                Delete
              </Button>
            )}
          </div>
            )}
          />
        </div>
        )}

      {/* Stage Context — prominent CTA and status per pipeline stage */}
      <StageContextCard lead={lead} onNavigate={navigate} />
  
        {/* Main Content */}
        {canUseStatementAnalysis ? (
          <LeadStatementAnalyses leadId={lead.id} canUpdateLead={canUpdateLeads()} />
        ) : null}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Lead Information */}
          <div className="lg:col-span-2 space-y-6">
            {isEditing ? (
              <Card>
                <CardHeader>
                  <CardTitle>Edit Lead</CardTitle>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleSubmit} className="space-y-6">
                    {/* Business Information */}
                    <div>
                      <h3 className="text-sm font-medium text-gray-700 mb-3">Business Information</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Input name="dba_name" label="DBA Name" defaultValue={lead.dba_name || ''} />
                        <Input name="legal_name" label="Legal Name" defaultValue={lead.legal_name || ''} />
                        <Input name="website" label="Website" defaultValue={lead.website || ''} />
                        <div>
                          <label htmlFor="ownership_type" className="block text-sm font-medium text-gray-700 mb-1">Ownership Type</label>
                          <select
                            id="ownership_type"
                            name="ownership_type"
                            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                            defaultValue={lead.ownership_type || ''}
                          >
                            <option value="">Select...</option>
                            {OWNERSHIP_TYPES.map(type => (
                              <option key={type} value={type}>{type}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </div>
  
                    {/* Contact Information */}
                    <div>
                      <h3 className="text-sm font-medium text-gray-700 mb-3">Contact Information</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Input name="contact_name" label="Contact Name" defaultValue={lead.contact_name || ''} />
                        <Input name="contact_phone" label="Phone" defaultValue={lead.contact_phone || ''} />
                        <Input name="business_email" label="Email" type="email" defaultValue={lead.business_email || ''} />
                      </div>
                    </div>
  
                    {/* Business Profile */}
                    <div>
                      <h3 className="text-sm font-medium text-gray-700 mb-3">Business Profile</h3>
                      <div className="mb-4">
                        <label htmlFor="business_description" className="block text-sm font-medium text-gray-700 mb-1">Business Description</label>
                        <textarea
                          id="business_description"
                          name="business_description"
                          rows={3}
                          className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                          defaultValue={lead.business_description || ''}
                        />
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <Input name="years_in_business" label="Years in Business" type="number" defaultValue={lead.years_in_business || ''} />
                        <Input name="monthly_volume" label="Monthly Volume ($)" type="number" defaultValue={lead.monthly_volume || ''} />
                        <Input name="avg_ticket" label="Avg Ticket ($)" type="number" defaultValue={lead.avg_ticket || ''} />
                        <Input name="high_ticket" label="High Ticket ($)" type="number" defaultValue={lead.high_ticket || ''} />
                      </div>
                    </div>
  
                    {/* Processing Mix */}
                    <div>
                      <h3 className="text-sm font-medium text-gray-700 mb-3">Processing Mix (%)</h3>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <Input name="pct_card_swiped" label="Card Swiped" type="number" min="0" max="100" defaultValue={lead.pct_card_swiped || ''} />
                        <Input name="pct_ecommerce" label="E-commerce" type="number" min="0" max="100" defaultValue={lead.pct_ecommerce || ''} />
                        <Input name="pct_cnp" label="Card Not Present" type="number" min="0" max="100" defaultValue={lead.pct_cnp || ''} />
                      </div>
                    </div>
  
                    {/* Qualification */}
                    <div>
                      <h3 className="text-sm font-medium text-gray-700 mb-3">Qualification</h3>
                      <div className="flex gap-6">
                        <label className="flex items-center gap-2">
                          <input type="checkbox" name="has_tax_id" className="rounded border-gray-300" defaultChecked={lead.has_tax_id} />
                          <span className="text-sm text-gray-700">Has Tax ID</span>
                        </label>
                        <label className="flex items-center gap-2">
                          <input type="checkbox" name="has_checking_account" className="rounded border-gray-300" defaultChecked={lead.has_checking_account} />
                          <span className="text-sm text-gray-700">Has Checking Account</span>
                        </label>
                      </div>
                    </div>
  
                    {/* Lead Source */}
                    <div>
                      <label htmlFor="lead_source" className="block text-sm font-medium text-gray-700 mb-1">Lead Source</label>
                      <select
                        id="lead_source"
                        name="lead_source"
                        className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                        defaultValue={lead.lead_source || ''}
                      >
                        <option value="">Select...</option>
                        {Object.entries(LEAD_SOURCES).map(([key, label]) => (
                          <option key={key} value={key}>{label}</option>
                        ))}
                      </select>
                    </div>
  
                    {/* Actions */}
                    <div className="flex justify-end gap-3 pt-4 border-t">
                      <Button type="button" variant="secondary" onClick={() => setIsEditing(false)}>
                        Cancel
                      </Button>
                      <Button type="submit" isLoading={updateLead.isPending}>
                        Save Changes
                      </Button>
                    </div>
                  </form>
                </CardContent>
              </Card>
            ) : (
              <>
                {/* Business Information */}
                <Card>
                  <CardHeader>
                    <CardTitle>Business Information</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <dl className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <dt className="text-sm font-medium text-gray-500">DBA Name</dt>
                        <dd className="mt-1 text-sm text-gray-900">{lead.dba_name || '-'}</dd>
                      </div>
                      <div>
                        <dt className="text-sm font-medium text-gray-500">Legal Name</dt>
                        <dd className="mt-1 text-sm text-gray-900">{lead.legal_name || '-'}</dd>
                      </div>
                      <div>
                        <dt className="text-sm font-medium text-gray-500">Website</dt>
                        <dd className="mt-1 text-sm text-gray-900">
                          {lead.website ? (
                            <a href={lead.website} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                              {lead.website}
                            </a>
                          ) : '-'}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-sm font-medium text-gray-500">Ownership Type</dt>
                        <dd className="mt-1 text-sm text-gray-900">{lead.ownership_type || '-'}</dd>
                      </div>
                      <div className="md:col-span-2">
                        <dt className="text-sm font-medium text-gray-500">Business Description</dt>
                        <dd className="mt-1 text-sm text-gray-900">{lead.business_description || '-'}</dd>
                      </div>
                    </dl>
                  </CardContent>
                </Card>
  
                {/* Contact Information */}
                <Card>
                  <CardHeader>
                    <CardTitle>Contact Information</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <dl className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <dt className="text-sm font-medium text-gray-500">Contact Name</dt>
                        <dd className="mt-1 text-sm text-gray-900">{lead.contact_name || '-'}</dd>
                      </div>
                      <div>
                        <dt className="text-sm font-medium text-gray-500">Phone</dt>
                        <dd className="mt-1 text-sm text-gray-900">{lead.contact_phone || '-'}</dd>
                      </div>
                      <div>
                        <dt className="text-sm font-medium text-gray-500">Email</dt>
                        <dd className="mt-1 text-sm text-gray-900">{lead.business_email || '-'}</dd>
                      </div>
                    </dl>
                  </CardContent>
                </Card>
  
                {/* Business Profile */}
                <Card>
                  <CardHeader>
                    <CardTitle>Business Profile</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <dl className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div>
                        <dt className="text-sm font-medium text-gray-500">Years in Business</dt>
                        <dd className="mt-1 text-sm text-gray-900">{lead.years_in_business ?? '-'}</dd>
                      </div>
                      <div>
                        <dt className="text-sm font-medium text-gray-500">Monthly Volume</dt>
                        <dd className="mt-1 text-sm text-gray-900">{formatCurrencyOrDash(lead.monthly_volume)}</dd>
                      </div>
                      <div>
                        <dt className="text-sm font-medium text-gray-500">Avg Ticket</dt>
                        <dd className="mt-1 text-sm text-gray-900">{formatCurrencyOrDash(lead.avg_ticket)}</dd>
                      </div>
                      <div>
                        <dt className="text-sm font-medium text-gray-500">High Ticket</dt>
                        <dd className="mt-1 text-sm text-gray-900">{formatCurrencyOrDash(lead.high_ticket)}</dd>
                      </div>
                    </dl>
                    <div className="mt-4 pt-4 border-t">
                      <h4 className="text-sm font-medium text-gray-700 mb-3">Processing Mix</h4>
                      <dl className="grid grid-cols-3 gap-4">
                        <div>
                          <dt className="text-sm font-medium text-gray-500">Card Swiped</dt>
                          <dd className="mt-1 text-sm text-gray-900">{lead.pct_card_swiped ?? '-'}%</dd>
                        </div>
                        <div>
                          <dt className="text-sm font-medium text-gray-500">E-commerce</dt>
                          <dd className="mt-1 text-sm text-gray-900">{lead.pct_ecommerce ?? '-'}%</dd>
                        </div>
                        <div>
                          <dt className="text-sm font-medium text-gray-500">Card Not Present</dt>
                          <dd className="mt-1 text-sm text-gray-900">{lead.pct_cnp ?? '-'}%</dd>
                        </div>
                      </dl>
                    </div>
                  </CardContent>
                </Card>
  
              {showSharedSections && (
                <>
                  <AppointmentsPanel anchor={{ kind: 'lead', id: lead.id }} />

                  <AccountActivityPanel anchor={{ kind: 'lead', id: lead.id }} />

                  <NotesPanel anchor={{ kind: 'lead', id: lead.id }} />

                  {/* Document Center */}
                  <DocumentCenter context={{ kind: 'lead', lead }} />

                  {/* Pending Document Requests */}
                  <PendingDocumentRequests anchor={{ kind: 'lead', id: lead.id }} />
                </>
              )}
  
                {/* Application Data Summary */}
                {lead.application && (
                  <>
                    <BoardingReadinessPanel applicationId={lead.application.id} />
                    <ApplicationDataCard application={lead.application} />
                  </>
                )}
              </>
            )}
          </div>
  
          {/* Sidebar */}
          <div className="space-y-6">
            {/* What's Next — stage-aware sidebar with next action + blockers */}
            <WhatsNextPanel lead={lead} />
  
            {/* Assignment — inline dropdowns, no edit-mode required.
                These are the most frequent ops actions on a lead, so the
                sidebar exposes them directly. Auto-saves on selection. */}
            <Card>
              <CardHeader>
                <CardTitle>Assignment</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <InlineAssigneeSelect
                  label="Primary"
                  currentUser={lead.assigned_user ?? null}
                  excludeUserId={lead.assigned_to_secondary}
                  canEdit={canAssignLeads()}
                  saving={updateLead.isPending}
                  placeholder="Unassigned"
                  onChange={(userId) => handleAssigneeChange('assigned_to', userId)}
                />
                <InlineAssigneeSelect
                  label="Secondary"
                  currentUser={lead.assigned_user_secondary ?? null}
                  excludeUserId={lead.assigned_to}
                  canEdit={canAssignLeads()}
                  saving={updateLead.isPending}
                  placeholder="None"
                  onChange={(userId) => handleAssigneeChange('assigned_to_secondary', userId)}
                />
                {!canAssignLeads() && (
                  <p className="text-xs text-gray-400">
                    Contact operations to reassign this lead.
                  </p>
                )}
              </CardContent>
            </Card>
  
            {/* Qualification Card */}
            <Card>
              <CardHeader>
                <CardTitle>Qualification</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center gap-2">
                  {lead.has_tax_id ? (
                    <svg className="h-5 w-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  ) : (
                    <svg className="h-5 w-5 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  )}
                  <span className="text-sm text-gray-700">Has Tax ID</span>
                </div>
                <div className="flex items-center gap-2">
                  {lead.has_checking_account ? (
                    <svg className="h-5 w-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  ) : (
                    <svg className="h-5 w-5 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  )}
                  <span className="text-sm text-gray-700">Has Checking Account</span>
                </div>
              </CardContent>
            </Card>
  
            {/* Pipeline Events */}
            {lead.pipeline_events && lead.pipeline_events.length > 0 && (
              <PipelineHistory events={lead.pipeline_events} />
            )}
  
            {/* Quick Actions */}
            <Card>
              <CardHeader>
                <CardTitle>Quick Actions</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <Button variant="secondary" className="w-full" onClick={() => navigate(`/leads/${lead.id}/application`)}>
                  Open TurboApp
                </Button>
                {lead.merchant_id && (
                  <Button variant="secondary" className="w-full" onClick={() => navigate(`/merchants/${lead.merchant_id}`)}>
                    Open Merchant Account
                  </Button>
                )}
                <Button variant="outline" className="w-full" onClick={() => navigate('/leads')}>
                  Back to Leads
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
  
        {/* Delete Confirmation Modal */}
        {showDeleteConfirm && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
            <Card className="w-full max-w-md mx-4">
              <CardHeader>
                <CardTitle>Delete Lead</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-gray-600 mb-4">
                  Are you sure you want to delete this lead? This action cannot be undone.
                </p>
                <div className="flex justify-end gap-3">
                  <Button variant="secondary" onClick={() => setShowDeleteConfirm(false)}>
                    Cancel
                  </Button>
                  <Button variant="destructive" onClick={handleDelete} isLoading={deleteLead.isPending}>
                    Delete
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
  
        {/* Stage Transition Modal */}
        {showTransitionModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
            <Card className="w-full max-w-md mx-4">
              <CardHeader>
                <CardTitle>Change Pipeline Stage</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">New Stage</label>
                    <select
                      className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                      value={selectedStage}
                      onChange={(e) => setSelectedStage(e.target.value)}
                    >
                      <option value="">Select stage...</option>
                      {manualTransitionTargets(lead.stage).map((key) => (
                        <option key={key} value={key}>
                          {PIPELINE_STAGES[key as keyof typeof PIPELINE_STAGES]?.label ?? key}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Note (optional)</label>
                    <textarea
                      className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                      rows={3}
                      value={transitionNote}
                      onChange={(e) => setTransitionNote(e.target.value)}
                      placeholder="Add a note about this transition..."
                    />
                  </div>
                  {transitionError && (
                    <div style={{
                      padding: '8px 12px',
                      borderRadius: '6px',
                      backgroundColor: '#fef2f2',
                      border: '1px solid #fecaca',
                      color: '#991b1b',
                      fontSize: '13px',
                      lineHeight: 1.5,
                    }}>
                      {transitionError}
                    </div>
                  )}
                  <div className="flex justify-end gap-3">
                    <Button variant="secondary" onClick={() => setShowTransitionModal(false)}>
                      Cancel
                    </Button>
                    <Button
                      onClick={handleTransition}
                      disabled={!selectedStage}
                      isLoading={transitionStage.isPending}
                    >
                      Confirm
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
  
        {showCreateTicketModal && (
          <CreateTicketModal
            context={{
              leadId: lead.id,
              leadName: lead.dba_name ?? lead.legal_name ?? lead.id,
            }}
            onClose={() => setShowCreateTicketModal(false)}
          />
        )}
      </div>
  )
}

/**
 * Pipeline history with deduplication, expandable notes, and
 * stage-colored timeline dots.
 *
 * Consecutive identical from→to transitions (common during testing
 * or webhook retries) are collapsed into a single entry with a
 * count badge. Notes are shown in full (not truncated) and are
 * expandable on click for long content.
 */
function PipelineHistory({ events }: { events: PipelineEvent[] }) {
  const [expandedNotes, setExpandedNotes] = useState<Set<string>>(new Set())

  const toggleNote = (eventId: string) => {
    setExpandedNotes((prev) => {
      const next = new Set(prev)
      if (next.has(eventId)) next.delete(eventId)
      else next.add(eventId)
      return next
    })
  }

  // Deduplicate consecutive identical from→to pairs
  type GroupedEvent = {
    key: string
    event: PipelineEvent
    count: number
  }
  const grouped: GroupedEvent[] = []
  for (const event of events) {
    const key = `${event.from_stage ?? ''}→${event.to_stage}`
    const last = grouped[grouped.length - 1]
    if (last && last.key === key) {
      last.count++
    } else {
      grouped.push({ key, event, count: 1 })
    }
  }

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    })
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Pipeline History</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {grouped.slice(0, 20).map((g, i) => {
            const { event, count } = g
            const dotColor = STAGE_BORDER_COLORS[stageColor(event.to_stage)] ?? '#9ca3af'
            const isExpanded = expandedNotes.has(event.id)
            const noteIsLong = (event.note?.length ?? 0) > 80

            return (
              <div key={event.id + '-' + i} className="flex gap-3 text-sm">
                <div
                  className="flex-shrink-0 w-2.5 h-2.5 mt-1.5 rounded-full"
                  style={{ backgroundColor: dotColor }}
                />
                <div className="flex-1 min-w-0">
                  <p className="text-gray-900">
                    {event.from_stage && (
                      <>
                        <span className="font-medium">{formatStage(event.from_stage)}</span>
                        {' → '}
                      </>
                    )}
                    <span className="font-medium">{formatStage(event.to_stage)}</span>
                    {count > 1 && (
                      <span
                        style={{
                          display: 'inline-block',
                          marginLeft: '6px',
                          padding: '1px 6px',
                          borderRadius: '9999px',
                          backgroundColor: '#e5e7eb',
                          color: '#4b5563',
                          fontSize: '11px',
                          fontWeight: 600,
                        }}
                      >
                        ×{count}
                      </span>
                    )}
                  </p>
                  {event.note && (
                    <p
                      className={`text-gray-500 ${noteIsLong && !isExpanded ? 'cursor-pointer' : ''}`}
                      style={noteIsLong && !isExpanded ? {
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      } : undefined}
                      onClick={() => noteIsLong && toggleNote(event.id)}
                      title={noteIsLong && !isExpanded ? 'Click to expand' : undefined}
                    >
                      {event.note}
                    </p>
                  )}
                  <p className="text-gray-400 text-xs">
                    {formatDate(event.created_at)}
                    {event.triggered_by && event.triggered_by !== 'manual' && (
                      <span className="text-gray-300"> · {formatLabel(event.triggered_by)}</span>
                    )}
                    {event.user && ` · ${event.user.name}`}
                  </p>
                </div>
              </div>
            )
          })}
        </div>
      </CardContent>
    </Card>
  )
}
